import cld from '~/services/cloudinary/cloudinaryInstance.tsx'
// import { thumbnail } from '@cloudinary/url-gen/actions/resize'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from '~/components/ui/select.tsx'
import { AdvancedImage } from '@cloudinary/react'
import React, { useEffect, useState } from 'react'
import { cn } from '~/utils/misc.ts'

type Option<T> = T & {
	id: string
	value: string
	label: string
	imgSrc: string
	tags?: string
}

interface Props<T> {
	options: Option<T>[]
	specialOptions: Option<T>[]
	onSelect: (selectedOption: Option<T>) => void
	autoFocus?: boolean
}

function generateThumbnail(imgSrc: string) {
	return cld.image(imgSrc).quality('auto').format('auto')
	// .resize(thumbnail().width(100).height(100).crop('fill'))
}

export function SelectWithImage<T = object>(
	props: Props<T>,
): React.JSX.Element {
	const { options, specialOptions } = props
	const [selectedValue, setSelectedValue] = useState<string | null>(null)
	const [selectedOption, setSelectedOption] = useState<Option<T> | null>(null)
	const [allOptions, setAllOptions] = useState([...specialOptions, ...options])

	useEffect(() => {
		const sortedSpecialOptions = [...specialOptions].sort((a, b) =>
			a.label.toLowerCase().localeCompare(b.label),
		)

		const sortedOptions = [...options].sort((a, b) =>
			a.label.toLowerCase().localeCompare(b.label),
		)

		setAllOptions({
			specialOptions: sortedSpecialOptions,
			options: sortedOptions,
		})
	}, [options, specialOptions])

	useEffect(() => {
		if (selectedOption) props.onSelect(selectedOption)
	}, [selectedOption])

	const handleSelect = (value: string) => {
		const combinedOptions = [
			...allOptions.specialOptions,
			...allOptions.options,
		]
		const option = combinedOptions.find(option => option.value === value)
		if (option) {
			setSelectedOption(option)
			setSelectedValue(value)
		}
	}

	return (
		<Select value={selectedValue} onValueChange={handleSelect}>
			<SelectTrigger className="h-[65px]">
				{selectedValue ? (
					<SelectValue placeholder="Select a style" />
				) : (
					'Select a style...'
				)}
			</SelectTrigger>
			<SelectContent className="max-h-[600px] w-[300px] overflow-y-scroll bg-white p-0">
				<SelectGroup>
					<SelectLabel>Special</SelectLabel>
					{specialOptions.map(option => (
						<SelectItem
							key={option.id}
							value={option.value}
							className="hover:bg-gray-200"
						>
							<div className="flex flex-row">
								<AdvancedImage
									cldImg={generateThumbnail(option.imgSrc)}
									alt={`${option.label} thumbnail`}
									className="mr-2 inline-block h-12 w-12"
								/>
								<div className="flex flex-col">
									<span>{option.label}</span>
									<div className="flex flex-row flex-wrap space-x-2">
										{option.tags?.split(',').map(tag => (
											<span
												key={tag}
												className={cn('text-xs', 'text-gray-500')}
											>
												{tag}
											</span>
										))}
									</div>
								</div>
							</div>
						</SelectItem>
					))}
				</SelectGroup>
				<SelectSeparator />
				<SelectGroup>
					<SelectLabel>Styles</SelectLabel>
					{options.map(option => (
						<SelectItem
							key={option.id}
							value={option.value}
							className="hover:bg-gray-200"
						>
							<div className="flex flex-row">
								<AdvancedImage
									cldImg={generateThumbnail(option.imgSrc)}
									alt={`${option.label} thumbnail`}
									className="mr-2 inline-block h-12 w-12"
								/>
								<div className="flex flex-col">
									<span>{option.label}</span>
									<div className="flex flex-row flex-wrap space-x-2">
										{option.tags?.split(',').map(tag => (
											<span
												key={tag}
												className={cn('text-xs', 'text-gray-500')}
											>
												{tag}
											</span>
										))}
									</div>
								</div>
							</div>
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	)
}
